import { useState, useEffect } from 'react'
import api from '@/services/api'
import { opportunitiesService } from '@/services/opportunities-service'

interface Estimator {
  id: string
  displayName: string
  email: string
}

interface Props {
  opportunityId: string
  opportunityTitle: string
  onClose: () => void
  onAccepted: (updated?: any) => void
}

export function AcceptOpportunityModal({
  opportunityId,
  opportunityTitle,
  onClose,
  onAccepted,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [loadingEstimators, setLoadingEstimators] = useState(true)
  const [estimators, setEstimators] = useState<Estimator[]>([])
  const [selectedEstimatorId, setSelectedEstimatorId] = useState('')
  const [notes, setNotes] = useState('')

  useEffect(() => {
    const fetchEstimators = async () => {
      try {
        setLoadingEstimators(true)
        const response = await api.get('/users?role=estimator')
        setEstimators(response.data?.data || [])
        // Auto-select first estimator if available
        if (response.data?.data?.length > 0) {
          setSelectedEstimatorId(response.data.data[0].id)
        }
      } catch (err) {
        console.error('Failed to fetch estimators:', err)
      } finally {
        setLoadingEstimators(false)
      }
    }

    fetchEstimators()
  }, [])

  const handleAccept = async () => {
    if (!selectedEstimatorId) return

    try {
      setLoading(true)
      await opportunitiesService.accept(opportunityId, selectedEstimatorId, notes || undefined)
      // Re-fetch the full opportunity detail so the page updates with new status
      const updated = await opportunitiesService.get(opportunityId)
      onAccepted(updated)
    } catch (err) {
      console.error('Failed to accept opportunity:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="opp-modal-overlay">
      <div className="opp-modal-card" style={{ maxWidth: '480px' }}>
        <button className="opp-modal-close" onClick={onClose} disabled={loading}>
          ✕
        </button>

        <h2 className="opp-modal-title">Accept Opportunity</h2>

        <p style={{ color: 'var(--s600)', marginBottom: '1.5rem' }}>
          Assign this opportunity to an estimator to begin the estimate.
        </p>

        {/* Opportunity Title Display */}
        <div className="opp-form-group">
          <label className="opp-form-label">Opportunity</label>
          <div style={{
            padding: '0.75rem',
            backgroundColor: 'var(--s50)',
            borderRadius: '0.5rem',
            color: 'var(--s800)',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            {opportunityTitle}
          </div>
        </div>

        {/* Estimator Selection */}
        <div className="opp-form-group">
          <label className="opp-form-label">Assign to Estimator *</label>
          {loadingEstimators ? (
            <div style={{ padding: '0.75rem', color: 'var(--s600)', fontSize: '0.875rem' }}>
              Loading estimators...
            </div>
          ) : (
            <select
              className="opp-form-select"
              value={selectedEstimatorId}
              onChange={e => setSelectedEstimatorId(e.target.value)}
              disabled={loading || estimators.length === 0}
            >
              <option value="">Select an estimator</option>
              {estimators.map(est => (
                <option key={est.id} value={est.id}>
                  {est.displayName} ({est.email})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Notes */}
        <div className="opp-form-group">
          <label className="opp-form-label">Notes (optional)</label>
          <textarea
            className="opp-form-textarea"
            placeholder="Any additional context for the estimator..."
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Actions */}
        <div className="opp-modal-actions">
          <button
            className="opp-modal-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="opp-modal-btn-primary"
            onClick={handleAccept}
            disabled={!selectedEstimatorId || loading || loadingEstimators}
          >
            {loading ? 'Accepting...' : 'Accept & Create Estimate'}
          </button>
        </div>
      </div>
    </div>
  )
}
